import { hotkeyHint } from "@/hooks/useCoverageBetaHotkeys";
import { useFetchImgObjUrl } from "@/hooks/useFetchImgObjUrl";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { cn } from "@/utils/utils";
import { OoSParcelSchema } from "@augmodo/lebron-browser-client";
import { ProductSchema } from "@labeling-tools/api/atlas";
import { mvpApiGetProductOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { ActionIcon, Text } from "@mantine/core";
import { IconBan, IconCheck, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

interface Props {
  parcelResp: OoSParcelSchema | undefined;
}

const MAX_CANDIDATES = 5;

/**
 * Inline "confirm product" step, rendered in the right footer (where the
 * selected product shows). Opens when a product is selected via the image.
 * Shows the clicked detection's top-N candidate products
 * (references[refImgIdx].product_ids_top_n[detectionIdx]) plus "No match"; the
 * picked product becomes the area's confirmed product (overwrites
 * confirmation_product_id at submit). Renders nothing when not confirming.
 */
export const ConfirmProductSelectionPanel = (props: Props) => {
  const { parcelResp } = props;

  const confirming = useCoverageBetaStore((s) => s.confirming);
  const confirmReferenceProduct = useCoverageBetaStore(
    (s) => s.confirmReferenceProduct,
  );
  const rejectConfirmingSelection = useCoverageBetaStore(
    (s) => s.rejectConfirmingSelection,
  );
  const recordInteraction = useCoverageBetaStore((s) => s.recordInteraction);
  const cancelConfirmingSelection = useCoverageBetaStore(
    (s) => s.cancelConfirmingSelection,
  );
  // The product *explicitly confirmed* for this detection. Scoped to the
  // detection being confirmed (not the area-level id, which would leak a check
  // across detections) and gated on the `confirmed` flag so the provisional
  // model default added on first click doesn't show as pre-confirmed.
  const confirmedProductId = useCoverageBetaStore((s) => {
    if (confirming == null) return null;
    const area = s.idxToFeEmptyArea[confirming.emptyAreaIdx];
    const selected = area?.selectedProducts.find(
      (p) =>
        p.source === "reference" &&
        p.referenceImgIdx === confirming.referenceImgIdx &&
        p.referenceProductDetectionIdx ===
          confirming.referenceProductDetectionIdx,
    );
    return selected?.source === "reference" && selected.confirmed
      ? selected.id
      : null;
  });

  // Flash/shake the panel when the user attempts something blocked by this
  // confirm step, so the reason their click did nothing is obvious.
  const confirmNudge = useCoverageBetaStore((s) => s.confirmNudge);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!confirmNudge) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 450);
    return () => clearTimeout(t);
  }, [confirmNudge]);

  const references = parcelResp?.references ?? [];

  // Top-N candidate products for the clicked reference detection, deduped by
  // product_id (the top-N list can repeat a product) so we show up to
  // MAX_CANDIDATES distinct products.
  const candidateProductIds = useMemo(() => {
    if (confirming == null) return [];
    const ref = references[confirming.referenceImgIdx] as
      | { product_ids_top_n?: (string | null)[][] }
      | undefined;
    const topN =
      ref?.product_ids_top_n?.[confirming.referenceProductDetectionIdx] ?? [];
    const ids: string[] = [];
    for (const pid of topN) {
      if (pid && !ids.includes(pid)) ids.push(pid);
      if (ids.length >= MAX_CANDIDATES) break;
    }
    return ids;
  }, [confirming, references]);

  if (confirming == null) return null;

  const emptyAreaIdx = confirming.emptyAreaIdx;
  // Candidates + the "No match" card. Cap columns at 4 so cells stay a readable
  // width; extra cards wrap to a second row (the confirm area is taller).
  const cardCount = candidateProductIds.length + 1;
  const gridCols = Math.min(cardCount, 4);

  return (
    <>
      <style>{`@keyframes cb-confirm-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
}`}</style>
      <div
        className={cn(
          "h-full flex flex-col gap-1 rounded-md border p-2 transition-colors",
          flash
            ? "border-blue-500 bg-blue-100 ring-2 ring-blue-400"
            : "border-blue-300 bg-blue-50",
        )}
        style={
          flash ? { animation: "cb-confirm-shake 0.4s ease-in-out" } : undefined
        }
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <Text size="xs" fw={700} className="leading-tight">
              Which product is in empty area {confirming.emptyAreaIdx}?
            </Text>
            <Text size="xs" c="dimmed" className="leading-tight">
              Pick the match below or “No match”. Other areas unlock once this
              is set.
            </Text>
          </div>
          <ActionIcon
            className="ml-auto shrink-0"
            size="sm"
            variant="subtle"
            color="gray"
            onClick={cancelConfirmingSelection}
            title="Cancel — remove this selection"
          >
            <IconX size={14} />
          </ActionIcon>
        </div>

        <div
          className="grid gap-3 flex-1 min-h-0 overflow-y-auto"
          style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}
        >
          {candidateProductIds.map((pid) => (
            <ConfirmProductCard
              key={pid}
              productId={pid}
              isConfirmed={confirmedProductId === pid}
              onConfirm={(product) =>
                confirmReferenceProduct(emptyAreaIdx, product)
              }
            />
          ))}

          {/* "No match" presented as an equal option alongside the candidates. */}
          <NoMatchCard
            hotkeyHint={hotkeyHint("no_match")}
            onClick={() => {
              rejectConfirmingSelection();
              recordInteraction("no_match", "click");
            }}
          />
        </div>
      </div>
    </>
  );
};

const NoMatchCard = ({
  onClick,
  hotkeyHint,
}: {
  onClick: () => void;
  hotkeyHint?: string;
}) => (
  <button
    onClick={onClick}
    title={
      hotkeyHint
        ? `None of these match — remove this product (${hotkeyHint})`
        : "None of these match — remove this product"
    }
    className="border-2 border-red-300 rounded p-1 text-left bg-red-50 hover:bg-red-100 hover:border-red-400 flex flex-col gap-0.5 overflow-hidden"
  >
    <div className="flex-1 min-h-0 bg-red-100 rounded flex items-center justify-center">
      <IconBan size={24} className="text-red-500" />
    </div>
    <div className="flex items-center justify-center gap-0.5">
      <Text size="sm" fw={600} c="red" className="leading-tight">
        No match
      </Text>
      {!!hotkeyHint && (
        <kbd className="text-[10px] font-mono text-red-400 border border-red-300 rounded px-1">
          {hotkeyHint}
        </kbd>
      )}
    </div>
  </button>
);

const ConfirmProductCard = (props: {
  productId: string;
  isConfirmed: boolean;
  onConfirm: (product: ProductSchema) => void;
}) => {
  const { productId, isConfirmed, onConfirm } = props;

  const { data: product } = useQuery({
    ...mvpApiGetProductOptions({ path: { product_id: productId } }),
    enabled: !!productId,
  });
  const { data: imgObjUrl } = useFetchImgObjUrl({
    img_id: productId,
    image_url: product?.image_url ?? "",
  });

  return (
    <button
      // Disabled until the product loads so the confirmed selection always
      // carries its description/image (not just the id).
      disabled={!product}
      onClick={() => product && onConfirm(product)}
      className={cn(
        "border rounded p-1 text-left hover:bg-gray-50 flex flex-col gap-0.5 overflow-hidden",
        isConfirmed ? "border-green-500 bg-green-50" : "border-gray-200",
      )}
    >
      <div className="flex-1 min-h-0 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
        {!!imgObjUrl && (
          <img src={imgObjUrl} className="object-contain h-full" />
        )}
      </div>
      <div className="flex items-center gap-0.5">
        {isConfirmed && (
          <IconCheck size={14} className="text-green-600 shrink-0" />
        )}
        <Text size="sm" lineClamp={4} className="leading-tight">
          {product?.description ?? "…"}
        </Text>
      </div>
    </button>
  );
};
